import React, { useCallback, useContext as useContextCore, useEffect } from 'react'
import type { DbRestoreStatus } from '@ztechapps/common.contract'
import { EnvironmentEnum } from '@ztechapps/common.contract'
import { useAllLocalStorage, useAllSessionStorage, useDbRestoreStatus } from '@ztechapps/common.hook'
import { getWindow } from '@ztechapps/common.utils'
import { DbRestoreModal } from './DbRestoreModal'

export type DbRestoreContextType = {
  enabled: boolean
  onStarted: (status: DbRestoreStatus) => void
}

const context = React.createContext<DbRestoreContextType>({} as DbRestoreContextType)

export type DbRestoreProviderProps = {
  children: React.ReactNode
  /** The app's deployment environment — the restore is unreachable in production or where this is unset. */
  environment?: string
  /** Where the browser lands when the restore is over — the app's login route. */
  loginPath: string
}

/**
 * While a restore is active it renders only the blocking modal — the app below is unmounted.
 * The provider itself sends no GraphQL (the restore drops the database, and the stale token would sign the user out
 * through the Apollo error link), so it signs out by clearing storage, never through `useUser().logout`.
 * The modal subscribes to `dbRestoreOutput` while it is mounted; a subscription error is handled in
 * `useDbRestoreOutput`, never by the HTTP error link, so the user stays signed in.
 */
export const DbRestoreProvider = ({ children, environment, loginPath }: DbRestoreProviderProps) => {
  // Fails closed: a missing environment disables the restore button and the status request.
  const enabled = !!environment && environment !== EnvironmentEnum.Production
  const { status, loading, running, failed, succeeded, interrupted, onStarted } = useDbRestoreStatus({ enabled })
  const localStorage = useAllLocalStorage()
  const sessionStorage = useAllSessionStorage()

  const onExit = useCallback(() => {
    localStorage.clear()
    sessionStorage.clear()
    getWindow().location.href = loginPath
  }, [localStorage, sessionStorage, loginPath])

  useEffect(() => {
    if (succeeded) onExit()
  }, [succeeded, onExit])

  const renderContent = () => {
    if (loading || succeeded) return null
    if (running || failed || interrupted) {
      return <DbRestoreModal status={status} failed={failed} interrupted={interrupted} onExit={onExit} />
    }
    return children
  }

  return <context.Provider value={{ enabled, onStarted }}>{renderContent()}</context.Provider>
}

export const useDbRestoreContext = () => useContextCore(context)
